/* ==========================================================================
   Sura – top categories grid
   Chevron navigation + optional auto-scroll for the horizontal card row.
   ========================================================================== */

(function () {
  if (customElements.get('sura-catgrid')) return;

  const EDGE = 2; // px tolerance when testing "at the start / at the end"

  class SuraCatGrid extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.sura-catgrid__list');
      if (!this.list) return;

      this.nav = this.querySelector('.sura-catgrid__nav');
      this.prevBtn = this.querySelector('[data-catgrid-dir="prev"]');
      this.nextBtn = this.querySelector('[data-catgrid-dir="next"]');

      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.autoplayMode = this.dataset.autoplay || 'off'; // off | mobile | all
      this.interval = Math.max(2, parseFloat(this.dataset.interval) || 4) * 1000;
      this.blockers = new Set();
      this.timer = null;
      this.frame = null;

      this.handleScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.refresh();
        });
      };

      this.list.addEventListener('scroll', this.handleScroll, { passive: true });

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.step(-1, { page: true, user: true }));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.step(1, { page: true, user: true }));

      // Autoplay never fights the person using the row.
      this.addEventListener('pointerenter', () => this.pause('pointer'));
      this.addEventListener('pointerleave', () => this.resume('pointer'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', () => this.resume('focus'));
      this.list.addEventListener('touchstart', () => this.pause('touch'), { passive: true });
      this.list.addEventListener('touchend', () => this.resume('touch'), { passive: true });

      this.handleVisibility = () => (document.hidden ? this.pause('tab') : this.resume('tab'));
      document.addEventListener('visibilitychange', this.handleVisibility);

      // Only run while the section is actually on screen.
      if ('IntersectionObserver' in window) {
        this.pause('offscreen');
        this.io = new IntersectionObserver(
          (entries) => {
            entries[0].isIntersecting ? this.resume('offscreen') : this.pause('offscreen');
          },
          { threshold: 0.25 }
        );
        this.io.observe(this);
      }

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.refresh());
        this.ro.observe(this.list);
      } else {
        window.addEventListener('resize', this.handleScroll);
      }

      this.handleBreakpoint = () => {
        this.refresh();
        this.restart();
      };
      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.handleBreakpoint);
        this.motionQuery.addEventListener('change', this.handleBreakpoint);
      }

      this.refresh();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      document.removeEventListener('visibilitychange', this.handleVisibility);
      window.removeEventListener('resize', this.handleScroll);
      if (this.mobileQuery && this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.handleBreakpoint);
        this.motionQuery.removeEventListener('change', this.handleBreakpoint);
      }
    }

    /* State ---------------------------------------------------------------- */

    get maxScroll() {
      return this.list.scrollWidth - this.list.clientWidth;
    }

    get overflowing() {
      return this.maxScroll > EDGE;
    }

    get autoplayEnabled() {
      if (!this.overflowing) return false;
      if (this.motionQuery.matches) return false;
      if (this.autoplayMode === 'all') return true;
      if (this.autoplayMode === 'mobile') return this.mobileQuery.matches;
      return false;
    }

    refresh() {
      const max = this.maxScroll;
      const atStart = this.list.scrollLeft <= EDGE;
      const atEnd = this.list.scrollLeft >= max - EDGE;

      this.classList.toggle('is-scrollable', this.overflowing);
      if (this.nav) this.nav.hidden = !this.overflowing;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;

      this.autoplayEnabled ? this.startTimer() : this.stopTimer();
    }

    /* Scrolling ------------------------------------------------------------ */

    stepSize(page) {
      const item = this.list.querySelector('.sura-catgrid__item');
      if (!item) return this.list.clientWidth;

      const gap = parseFloat(getComputedStyle(this.list).columnGap) || 0;
      const column = item.getBoundingClientRect().width + gap;
      if (!page) return column;

      const perView = Math.max(1, Math.floor((this.list.clientWidth + gap) / column));
      return column * perView;
    }

    step(direction, options) {
      const opts = options || {};
      const max = this.maxScroll;
      let target = this.list.scrollLeft + this.stepSize(opts.page) * direction;

      if (!opts.user) {
        // Autoplay wraps around so the row keeps cycling.
        if (direction > 0 && this.list.scrollLeft >= max - EDGE) target = 0;
        if (direction < 0 && this.list.scrollLeft <= EDGE) target = max;
      }

      this.list.scrollTo({
        left: Math.max(0, Math.min(max, target)),
        behavior: this.motionQuery.matches ? 'auto' : 'smooth',
      });

      if (opts.user) this.restart();
    }

    /* Autoplay ------------------------------------------------------------- */

    startTimer() {
      if (this.timer || this.blockers.size || !this.autoplayEnabled) return;
      this.timer = setInterval(() => this.step(1, { page: false }), this.interval);
    }

    stopTimer() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stopTimer();
      this.startTimer();
    }

    pause(reason) {
      this.blockers.add(reason);
      this.stopTimer();
    }

    resume(reason) {
      this.blockers.delete(reason);
      if (!this.blockers.size) this.startTimer();
    }
  }

  customElements.define('sura-catgrid', SuraCatGrid);
})();
